/**
 * FieldFilterNav.tsx  —  scalable, faceted, virtualized field navigator
 * ─────────────────────────────────────────────────────────────────────────────
 * Drop-in replacement for <FieldNav>. Built for real DG scale: a single table
 * with tens of thousands of fields renders smoothly (custom windowing, no new
 * deps), and an SME can slice the list by the things that actually drive review.
 *
 * Facets are DATA-DERIVED (values present in the fields, with live counts), so
 * nothing is hardcoded or client-specific. Confidence bands come from the
 * SME-config model; anonymization bands come from dgConfig thresholds.
 */
import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { ConfModel, confLevel as confLevelOf, pct } from "./DGArtifactsConfidence";

// palette (subset of the app T tokens)
const K = {
  ink: "#0f1923", primary: "#1e2d3d", muted: "#64748b", label: "#2c3e52",
  brand: "#1e3a6e", brandLight: "#dde7f5", brandMid: "#90aed0",
  border: "#c8d4e0", sidebar: "#f4f6fa", bg: "#eef1f6", card: "#fff",
  green: "#166534", greenBg: "#f0fdf4", greenBorder: "#86efac",
  amber: "#9a3412", amberBg: "#fff7ed", amberBorder: "#fdba74",
  red: "#991b1b", redBg: "#fef2f2", redBorder: "#fca5a5",
};

const ROW_H = 54;
const HEADER_H = 30;
const OVERSCAN = 8;

// ── helpers ───────────────────────────────────────────────────────────────────
function confBand(field: any, model: ConfModel): "High" | "Medium" | "Low" | "Unscored" {
  const s = field?.field_confidence?.score;
  if (s == null || isNaN(s)) return "Unscored";
  return confLevelOf(s, model) as "High" | "Medium" | "Low";
}
function bandTone(b: string) {
  if (b === "High") return { color: K.green, bg: K.greenBg, border: K.greenBorder };
  if (b === "Medium") return { color: K.amber, bg: K.amberBg, border: K.amberBorder };
  if (b === "Low") return { color: K.red, bg: K.redBg, border: K.redBorder };
  return { color: K.muted, bg: K.bg, border: K.border };
}
// weakest attribute below the review threshold (drives "which value is shaky")
function weakestAttr(field: any, model: ConfModel): string | null {
  const cc = field?.classification_confidence;
  if (!cc || typeof cc !== "object") return null;
  let worstKey: string | null = null, worst = Infinity;
  for (const a of model.attributes) {
    const c = cc[a.confKey]?.confidence;
    if (typeof c === "number" && c < worst) { worst = c; worstKey = a.confKey; }
  }
  if (worstKey == null || worst >= model.reviewBelow) return null;
  return model.labels[worstKey] || worstKey;
}
// anonymization band from dgConfig thresholds (config-driven), else score buckets
function riskBand(field: any, dgConfig: any): string {
  const rec = field?.anonymization_decision?.recommendation;
  if (rec) return rec;
  const score = field?.anonymization_assessment?.final_score;
  if (score == null) return "—";
  const ths = dgConfig?.anonymization_scoring_model?.thresholds;
  if (Array.isArray(ths)) {
    for (const t of ths) if (score >= (t.min_score ?? -Infinity) && score <= (t.max_score ?? Infinity)) return t.recommendation || "—";
  }
  return score >= 4 ? "Anonymize" : score >= 2 ? "Review" : "Do Not Anonymize";
}
const asArray = (v: any): string[] =>
  v == null || v === "" ? [] : Array.isArray(v) ? v.filter(Boolean).map(String) : [String(v)];

// ── faceted filter definitions (data-derived) ────────────────────────────────
interface Facet { id: string; label: string; open: boolean; get: (f: any) => string | string[] | null; }

export default function FieldFilterNav({
  fields, selectedIdx, approvedSet, onSelect, progress, model, dgConfig,
  activeFilter, onClearFilter, entityLabel,
}: {
  fields: any[]; selectedIdx: number; approvedSet: Set<string>;
  onSelect: (i: number) => void; progress: number;
  model: ConfModel; dgConfig?: any;
  activeFilter?: { type: string; value: string } | null;
  onClearFilter?: () => void; entityLabel?: string;
}) {
  const facets: Facet[] = useMemo(() => [
    { id: "confidence", label: "Confidence", open: true,  get: f => confBand(f, model) },
    { id: "weak",       label: "Weakest value (< review)", open: false, get: f => weakestAttr(f, model) },
    { id: "status",     label: "Review status", open: true, get: f => (approvedSet.has(f.field) ? "Approved" : "Pending") },
    { id: "cde",        label: "CDE tier", open: true, get: f => (f.is_cde === true ? (f.cde_tier || "CDE") : "Non-CDE") },
    { id: "risk",       label: "Anonymization", open: false, get: f => riskBand(f, dgConfig) },
    { id: "sensitivity",label: "Sensitivity", open: false, get: f => f.classification?.sensitivity || "—" },
    { id: "privacy",    label: "Privacy category", open: false, get: f => f.classification?.privacy_category || "—" },
    { id: "regulatory", label: "Regulatory", open: false, get: f => asArray(f.classification?.regulatory_frameworks) },
    { id: "ownership",  label: "Ownership", open: false, get: f => f.classification?.ownership || "—" },
    { id: "valueType",  label: "Value type", open: false, get: f => f.business_value_type || f.value_type || "—" },
  ], [model, dgConfig, approvedSet]);

  // ── filter state ─────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<Record<string, Set<string>>>({});
  const [preset, setPreset] = useState<string | null>(null);
  const [sort, setSort] = useState<"review" | "conf_asc" | "conf_desc" | "risk" | "name" | "status">("review");
  const [showFilters, setShowFilters] = useState(true);
  const [openFacets, setOpenFacets] = useState<Set<string>>(() => new Set(facets.filter(f => f.open).map(f => f.id)));
  const [expandedFacet, setExpandedFacet] = useState<Set<string>>(new Set());

  const toggleVal = (fid: string, v: string) => setSel(p => {
    const n = { ...p }; const s = new Set(n[fid] ?? []); s.has(v) ? s.delete(v) : s.add(v);
    if (s.size) n[fid] = s; else delete n[fid]; return n;
  });
  const clearAll = () => { setSel({}); setPreset(null); setSearch(""); onClearFilter?.(); };

  // ── preset predicates (SME worklists) ────────────────────────────────────
  const presetFns: Record<string, (f: any) => boolean> = useMemo(() => ({
    needs: f => !approvedSet.has(f.field) &&
      (confBand(f, model) === "Low" || f.is_cde === true ||
       ["Anonymize", "Review"].includes(riskBand(f, dgConfig))),
    low: f => confBand(f, model) === "Low",
    pending: f => !approvedSet.has(f.field),
    approved: f => approvedSet.has(f.field),
    cde: f => f.is_cde === true,
    regulated: f => asArray(f.classification?.regulatory_frameworks).some(r => r && r !== "Not Applicable"),
    rule: f => { const cc = f.classification_confidence || {}; return Object.values(cc).some((e: any) => e?.source === "rule"); },
  }), [approvedSet, model, dgConfig]);

  // ── external (crosswalk) predicate for backward compat ───────────────────
  const externalPred = useCallback((f: any) => {
    if (!activeFilter) return true;
    const t = activeFilter.type, v = activeFilter.value;
    if (t === "Sensitivity") return (f.classification?.sensitivity ?? "") === v;
    if (t === "CDE Tier") return f.cde_tier === v;
    if (t === "Steward") return f.steward_role === v;
    if (t === "Criticality") return f.criticality === v;
    if (t === "Privacy") return f.contains_personal_data === true;
    if (t === "Pending") return !approvedSet.has(f.field);
    return true;
  }, [activeFilter, approvedSet]);

  // indexable field records
  const records = useMemo(() => fields.map((f, idx) => ({ f, idx })), [fields]);

  // search + preset + external pass (independent of facets) — used for facet counts
  const basePass = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pfn = preset ? presetFns[preset] : null;
    return records.map(({ f }) => {
      if (q) {
        const hay = `${f.display_name ?? ""} ${f.field ?? ""} ${f.business_term ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (pfn && !pfn(f)) return false;
      if (!externalPred(f)) return false;
      return true;
    });
  }, [records, search, preset, presetFns, externalPred]);

  // per-field facet values
  const facetVals = useMemo(
    () => records.map(({ f }) => {
      const m: Record<string, string[]> = {};
      for (const fc of facets) { const v = fc.get(f); m[fc.id] = v == null ? [] : Array.isArray(v) ? v : [v]; }
      return m;
    }),
    [records, facets]
  );

  const facetMatch = useCallback((i: number, skip?: string) => {
    for (const fc of facets) {
      if (fc.id === skip) continue;
      const chosen = sel[fc.id];
      if (!chosen || chosen.size === 0) continue;
      const vals = facetVals[i][fc.id];
      if (!vals.some(v => chosen.has(v))) return false;
    }
    return true;
  }, [facets, sel, facetVals]);

  // ── filtered + sorted records ────────────────────────────────────────────
  const filtered = useMemo(() => {
    const out: { f: any; idx: number }[] = [];
    for (let i = 0; i < records.length; i++) if (basePass[i] && facetMatch(i)) out.push(records[i]);
    const sevRisk = (f: any) => ({ "Anonymize": 3, "Review": 2 } as any)[riskBand(f, dgConfig)] ?? 0;
    const cscore = (f: any) => f.field_confidence?.score ?? 2; // unscored sorts as high
    const cmp: Record<string, (a: any, b: any) => number> = {
      review: (a, b) => (Number(approvedSet.has(a.f.field)) - Number(approvedSet.has(b.f.field)))
        || (cscore(a.f) - cscore(b.f)) || (sevRisk(b.f) - sevRisk(a.f)),
      conf_asc: (a, b) => cscore(a.f) - cscore(b.f),
      conf_desc: (a, b) => cscore(b.f) - cscore(a.f),
      risk: (a, b) => sevRisk(b.f) - sevRisk(a.f),
      name: (a, b) => String(a.f.display_name ?? a.f.field).localeCompare(String(b.f.display_name ?? b.f.field)),
      status: (a, b) => Number(approvedSet.has(a.f.field)) - Number(approvedSet.has(b.f.field)),
    };
    out.sort(cmp[sort]);
    return out;
  }, [records, basePass, facetMatch, sort, approvedSet, dgConfig]);

  // ── facet counts (respect all OTHER active facets — proper faceting) ─────
  const facetCounts = useMemo(() => {
    const counts: Record<string, Map<string, number>> = {};
    for (const fc of facets) counts[fc.id] = new Map();
    for (let i = 0; i < records.length; i++) {
      if (!basePass[i]) continue;
      for (const fc of facets) {
        if (!facetMatch(i, fc.id)) continue;
        for (const v of facetVals[i][fc.id]) counts[fc.id].set(v, (counts[fc.id].get(v) ?? 0) + 1);
      }
    }
    return counts;
  }, [facets, records, basePass, facetMatch, facetVals]);

  const activeCount = Object.values(sel).reduce((n, s) => n + s.size, 0) + (preset ? 1 : 0) + (activeFilter ? 1 : 0);

  // ── virtualization ────────────────────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(600);
  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setViewH(el.clientHeight));
    ro.observe(el); setViewH(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  // filter popover (keeps facets OFF the rail so the list gets the height)
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!popoverOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setPopoverOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setPopoverOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [popoverOpen]);

  const total = filtered.length * ROW_H;
  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end = Math.min(filtered.length, Math.ceil((scrollTop + viewH) / ROW_H) + OVERSCAN);
  const slice = filtered.slice(start, end);

  // keep the selected field in view when it changes externally
  useEffect(() => {
    const pos = filtered.findIndex(r => r.idx === selectedIdx);
    if (pos < 0 || !scrollRef.current) return;
    const top = pos * ROW_H, bottom = top + ROW_H;
    const el = scrollRef.current;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (bottom > el.scrollTop + el.clientHeight) el.scrollTop = bottom - el.clientHeight;
  }, [selectedIdx]); // eslint-disable-line

  // keyboard up/down through the *filtered* order
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const pos = filtered.findIndex(r => r.idx === selectedIdx);
    const next = e.key === "ArrowDown" ? Math.min(filtered.length - 1, pos + 1) : Math.max(0, pos - 1);
    if (filtered[next]) onSelect(filtered[next].idx);
  };

  const PRESETS: { id: string; label: string }[] = [
    { id: "needs", label: "Needs review" }, { id: "low", label: "Low confidence" },
    { id: "pending", label: "Unapproved" }, { id: "cde", label: "CDE" },
    { id: "regulated", label: "Regulated" }, { id: "rule", label: "Rule-driven" },
    { id: "approved", label: "Approved" },
  ];

  const facetPanel = (
    <>
      {/* quick views inside the panel */}
      <div style={{ padding: "10px 12px", borderBottom: `1px solid ${K.border}` }}>
        <div style={{ fontSize: "0.62rem", fontWeight: 700, color: K.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Quick views</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {PRESETS.map(p => {
            const on = preset === p.id;
            return (
              <button key={p.id} onClick={() => setPreset(on ? null : p.id)}
                style={{ fontSize: "0.64rem", fontWeight: 700, padding: "3px 9px", borderRadius: 20, cursor: "pointer",
                  background: on ? K.brand : K.card, color: on ? "#fff" : K.brand, border: `1px solid ${on ? K.brand : K.brandMid}` }}>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      {/* facets */}
      <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        {facets.map(fc => {
          const counts = facetCounts[fc.id];
          const opts = [...counts.entries()].sort((a, b) => b[1] - a[1]);
          if (opts.length === 0) return null;
          const isOpen = openFacets.has(fc.id);
          const showAll = expandedFacet.has(fc.id);
          const shown = showAll ? opts : opts.slice(0, 6);
          return (
            <div key={fc.id} style={{ borderBottom: `1px solid ${K.border}` }}>
              <button onClick={() => setOpenFacets(p => { const n = new Set(p); n.has(fc.id) ? n.delete(fc.id) : n.add(fc.id); return n; })}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "none", border: "none", cursor: "pointer" }}>
                <span style={{ fontSize: "0.66rem", fontWeight: 700, color: K.label, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {fc.label}{sel[fc.id]?.size ? <span style={{ marginLeft: 6, color: K.brand }}>({sel[fc.id]!.size})</span> : null}
                </span>
                <span style={{ fontSize: "0.6rem", color: K.muted, transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>▾</span>
              </button>
              {isOpen && (
                <div style={{ padding: "0 10px 8px" }}>
                  {shown.map(([v, c]) => {
                    const on = sel[fc.id]?.has(v) ?? false;
                    const tone = fc.id === "confidence" ? bandTone(v) : null;
                    return (
                      <label key={v} style={{ display: "flex", alignItems: "center", gap: 7, padding: "3px 4px", borderRadius: 5, cursor: "pointer", background: on ? K.brandLight : "transparent" }}>
                        <input type="checkbox" checked={on} onChange={() => toggleVal(fc.id, v)} style={{ accentColor: K.brand, cursor: "pointer" }} />
                        {tone && <span style={{ width: 7, height: 7, borderRadius: "50%", background: tone.color }} />}
                        <span style={{ fontSize: "0.72rem", color: K.primary, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
                        <span style={{ fontSize: "0.64rem", fontWeight: 700, color: K.muted, fontVariantNumeric: "tabular-nums" }}>{c}</span>
                      </label>
                    );
                  })}
                  {opts.length > 6 && (
                    <button onClick={() => setExpandedFacet(p => { const n = new Set(p); n.has(fc.id) ? n.delete(fc.id) : n.add(fc.id); return n; })}
                      style={{ fontSize: "0.64rem", color: K.brand, background: "none", border: "none", cursor: "pointer", padding: "3px 4px" }}>
                      {showAll ? "Show less" : `+${opts.length - 6} more`}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, padding: "9px 12px", borderTop: `1px solid ${K.border}` }}>
        <button onClick={clearAll} style={{ flex: 1, fontSize: "0.68rem", fontWeight: 700, color: K.muted, background: K.card, border: `1px solid ${K.border}`, borderRadius: 6, padding: "6px 0", cursor: "pointer" }}>Clear all</button>
        <button onClick={() => setPopoverOpen(false)} style={{ flex: 1, fontSize: "0.68rem", fontWeight: 700, color: "#fff", background: K.brand, border: `1px solid ${K.brand}`, borderRadius: 6, padding: "6px 0", cursor: "pointer" }}>Done</button>
      </div>
    </>
  );

  return (
    <div style={{ width: 288, flexShrink: 0, background: K.sidebar, borderRight: `1px solid ${K.border}`, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}
         onKeyDown={onKeyDown} tabIndex={0}>
      {/* progress (compact) */}
      <div style={{ padding: "10px 14px 9px", borderBottom: `1px solid ${K.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: "0.62rem", fontWeight: 700, color: K.label, textTransform: "uppercase", letterSpacing: "0.07em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {entityLabel ?? "Review progress"}
          </span>
          <span style={{ fontSize: "0.72rem", fontWeight: 700, color: progress === 100 ? K.green : K.brand, flexShrink: 0 }}>
            {approvedSet.size}/{fields.length} · {progress}%
          </span>
        </div>
        <div style={{ height: 3, background: K.border, borderRadius: 99, overflow: "hidden" }}>
          <div style={{ width: `${progress}%`, height: "100%", borderRadius: 99, background: progress === 100 ? K.green : K.brand, transition: "width 0.4s" }} />
        </div>
      </div>

      {/* search */}
      <div style={{ padding: "9px 12px 7px" }}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: "0.8rem", color: K.muted }}>⌕</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search field or business term…"
            style={{ width: "100%", boxSizing: "border-box", padding: "7px 26px", fontSize: "0.76rem", borderRadius: 7, border: `1px solid ${K.border}`, background: K.card, color: K.ink, outline: "none" }} />
          {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", border: "none", background: "none", cursor: "pointer", color: K.muted, fontSize: "0.8rem" }}>✕</button>}
        </div>
      </div>

      {/* preset chips — single horizontally-scrolling row (fixed height) */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 12px 15px", overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap", scrollbarWidth: "none" }}>
        {PRESETS.map(p => {
          const on = preset === p.id;
          return (
            <button key={p.id} onClick={() => setPreset(on ? null : p.id)}
              style={{ flexShrink: 0, fontSize: "0.66rem", fontWeight: 700, lineHeight: 1.5, padding: "5px 12px", borderRadius: 20, cursor: "pointer",
                background: on ? K.brand : K.card, color: on ? "#fff" : K.brand, border: `1px solid ${on ? K.brand : K.brandMid}` }}>
              {p.label}
            </button>
          );
        })}
      </div>

      {/* toolbar: Filters button · sort · count */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 12px 8px", borderBottom: `1px solid ${K.border}` }}>
        <button ref={btnRef} onClick={() => setPopoverOpen(o => !o)}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.7rem", fontWeight: 700, padding: "6px 11px", borderRadius: 7, cursor: "pointer", lineHeight: 1.4,
            background: (activeCount > 0 || popoverOpen) ? K.brandLight : K.card, color: K.brand, border: `1px solid ${activeCount > 0 ? K.brandMid : K.border}` }}>
          <span style={{ fontSize: "0.82rem", lineHeight: 1 }}>⛃</span> Filters
          {activeCount > 0 && <span style={{ background: K.brand, color: "#fff", borderRadius: 20, fontSize: "0.6rem", padding: "0 6px" }}>{activeCount}</span>}
        </button>
        <select value={sort} onChange={e => setSort(e.target.value as any)} title="Sort"
          style={{ flex: 1, minWidth: 0, fontSize: "0.68rem", padding: "5px 6px", borderRadius: 7, border: `1px solid ${K.border}`, background: K.card, color: K.ink, cursor: "pointer" }}>
          <option value="review">Review priority</option>
          <option value="conf_asc">Confidence ↑</option>
          <option value="conf_desc">Confidence ↓</option>
          <option value="risk">Risk</option>
          <option value="name">Name A–Z</option>
          <option value="status">Status</option>
        </select>
      </div>

      {/* active filter chips — single scrolling row, only when present */}
      {activeCount > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", borderBottom: `1px solid ${K.border}`, overflowX: "auto", whiteSpace: "nowrap", scrollbarWidth: "none" }}>
          {preset && <Chip label={PRESETS.find(p => p.id === preset)?.label ?? preset} onClear={() => setPreset(null)} />}
          {activeFilter && <Chip label={`${activeFilter.type}: ${activeFilter.value}`} onClear={() => onClearFilter?.()} />}
          {Object.entries(sel).flatMap(([fid, ss]) => [...ss].map(v => <Chip key={fid + v} label={v} onClear={() => toggleVal(fid, v)} />))}
          <button onClick={clearAll} style={{ flexShrink: 0, marginLeft: 2, fontSize: "0.62rem", color: K.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Clear</button>
        </div>
      )}

      {/* result count */}
      <div style={{ padding: "6px 12px", fontSize: "0.64rem", color: K.muted, borderBottom: `1px solid ${K.border}` }}>
        Showing <strong style={{ color: K.ink }}>{filtered.length.toLocaleString()}</strong> of {fields.length.toLocaleString()} fields
      </div>

      {/* virtualized results — now gets the full remaining height */}
      <div ref={scrollRef} onScroll={e => setScrollTop((e.target as HTMLDivElement).scrollTop)} style={{ flexGrow: 1, overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "28px 16px", textAlign: "center", color: K.muted, fontSize: "0.74rem" }}>
            No fields match these filters.
            <div><button onClick={clearAll} style={{ marginTop: 8, fontSize: "0.7rem", color: K.brand, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Clear filters</button></div>
          </div>
        ) : (
          <div style={{ height: total, position: "relative" }}>
            {slice.map((r, i) => {
              const idx = r.idx, f = r.f, top = (start + i) * ROW_H;
              const isActive = idx === selectedIdx;
              const isApproved = approvedSet.has(f.field);
              const band = confBand(f, model);
              const tone = bandTone(band);
              const score = f.field_confidence?.score;
              const weak = weakestAttr(f, model);
              return (
                <div key={f.field ?? idx} onClick={() => onSelect(idx)} title={weak ? `Weakest value: ${weak}` : undefined}
                  style={{ position: "absolute", top, left: 0, right: 0, height: ROW_H, boxSizing: "border-box",
                    padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${K.border}`,
                    borderLeft: isActive ? `3px solid ${K.brand}` : "3px solid transparent",
                    background: isActive ? K.brandLight : "transparent" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: "0.79rem", fontWeight: isActive ? 700 : 500, color: isApproved ? K.muted : isActive ? K.brand : K.ink, textDecoration: isApproved ? "line-through" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.display_name ?? f.field}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                        {f.data_type && <span style={{ fontSize: "0.62rem", color: K.muted, fontFamily: "monospace" }}>{f.data_type}{f.data_length ? `(${f.data_length})` : ""}</span>}
                        {score != null && (
                          <span style={{ fontSize: "0.6rem", fontWeight: 700, color: tone.color, background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 20, padding: "0 6px", display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: tone.color }} />{pct(score)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                      {f.is_cde === true && <span title={`CDE · ${f.cde_tier ?? ""}`} style={{ fontSize: "0.56rem", fontWeight: 800, color: f.cde_tier === "Critical" ? K.red : K.amber, letterSpacing: "0.04em" }}>CDE</span>}
                      {band === "Low" && <span title="Needs review" style={{ fontSize: "0.62rem", color: K.red }}>⚑</span>}
                      {isApproved && <span style={{ fontSize: "0.66rem", color: K.green, fontWeight: 700 }}>✓</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* floating facet popover — overlays, does NOT consume rail height */}
      {popoverOpen && (
        <div ref={popRef} style={{ position: "absolute", top: 92, left: 8, width: 272, maxHeight: "calc(100% - 104px)",
          display: "flex", flexDirection: "column", background: K.card, border: `1px solid ${K.brandMid}`, borderRadius: 10,
          boxShadow: "0 12px 32px rgba(15,25,35,0.18)", zIndex: 60, overflow: "hidden" }}>
          {facetPanel}
        </div>
      )}
    </div>
  );
}

function Chip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.64rem", fontWeight: 600, padding: "2px 4px 2px 9px", borderRadius: 20, background: K.brandLight, color: K.brand, border: `1px solid ${K.brandMid}`, maxWidth: 160 }}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <button onClick={onClear} style={{ border: "none", background: "none", cursor: "pointer", color: K.brand, fontSize: "0.72rem", lineHeight: 1, padding: "0 2px" }}>✕</button>
    </span>
  );
}